import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Grid,
    Autocomplete,
    InputAdornment,
    Chip,
    TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import CrudService from "services/cruds-service";
import countryOptions from "../../components/List/Countries";
import { t } from "langs";
import { CrmPageHeader, CrmNotice, CrmActionButton, crmCardSx } from "../shared/CrmUi";
import { ReadOnlyBanner, ReadOnlyFieldset } from "components/ReadOnlyLock";

// getTagProps() key içerir; key spread ile verilmemeli (React 18.3 uyarısı), Chip key={...} ile alır
const omitKey = ({ key, ...rest }) => rest;

const CreateCompetitor = () => {
    const navigate = useNavigate();
    const { search } = useLocation();
    const competitorId = new URLSearchParams(search).get("id");
    const isEditing = Boolean(competitorId);
    // An existing competitor opens read-only, like the TMS pages; "Edit" unlocks the form.
    const [readOnlyMode, setReadOnlyMode] = useState(isEditing);
    useEffect(() => {
        setReadOnlyMode(isEditing);
    }, [isEditing, competitorId]);

    const [competitor, setCompetitor] = useState({
        competitorCompany: "",
        competitorRepresentative: "",
        competitorCustomer: [],
        competitorComment: "",
        competitorCustomerCountry: [],
        competitorProduct: [],
    });

    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState([]);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    // 🔄 çoklu ülke seçimi için local state
    const [selectedCountries, setSelectedCountries] = useState([]);

    /* ------------ fetch customers ------------ */
    useEffect(() => {
        CrudService.getCustomers()
            .then((resp) =>
                setCustomers(
                    resp.data.map(({ id, attributes }) => ({ id, name: attributes.name }))
                )
            )
            .catch(console.error);
    }, []);

    /* ------------ fetch competitor (edit) ------------ */
    useEffect(() => {
        if (!isEditing || !competitorId) return;
        setLoading(true);
        CrudService.getCompetitorById(competitorId)
            .then(({ data }) => {
                const at = data.attributes;
                setCompetitor({
                    competitorCompany: at.competitorCompany || "",
                    competitorRepresentative: at.competitorRepresentative || "",
                    competitorCustomer: Array.isArray(at.competitorCustomer)
                        ? at.competitorCustomer
                        : at.competitorCustomer
                            ? [at.competitorCustomer]
                            : [],
                    competitorComment: at.competitorComment || "",
                    competitorCustomerCountry: Array.isArray(at.competitorCustomerCountry)
                        ? at.competitorCustomerCountry
                        : at.competitorCustomerCountry
                            ? [at.competitorCustomerCountry]
                            : [],
                    competitorProduct: Array.isArray(at.competitorProduct)
                        ? at.competitorProduct
                        : at.competitorProduct?.split(",") || [],
                });
                // set selected countries for chips
                const matched = countryOptions.filter((c) =>
                    (Array.isArray(at.competitorCustomerCountry)
                            ? at.competitorCustomerCountry
                            : [at.competitorCustomerCountry]
                    ).includes(c.label)
                );
                setSelectedCountries(matched);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [isEditing, competitorId]);

    /* ------------ sync customer chips on edit ------------ */
    useEffect(() => {
        if (!isEditing || customers.length === 0) return;
        const matched = customers.filter((c) =>
            competitor.competitorCustomer.includes(c.id)
        );
        setSelectedCustomer(matched);
    }, [customers, competitor.competitorCustomer, isEditing]);

    /* ------------ handlers ------------ */
    const handleChange = ({ target: { name, value } }) =>
        setCompetitor((p) => ({ ...p, [name]: value }));

    const handleCustomerChange = (_e, newVal) => {
        setSelectedCustomer(newVal);
        setCompetitor((p) => ({
            ...p,
            competitorCustomer: newVal.map((c) => c.id),
        }));
    };

    /* 🔄 çoklu country handler */
    const handleCountryChange = (_e, newVal) => {
        setSelectedCountries(newVal);
        setCompetitor((p) => ({
            ...p,
            competitorCustomerCountry: newVal.map((c) => c.label),
        }));
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (isEditing) {
                await CrudService.updateCompetitor(competitor, competitorId);
                setSuccessMessage("Competitor updated successfully.");
            } else {
                await CrudService.createCompetitor({ data: competitor });
                setSuccessMessage("Competitor created successfully.");
            }
            window.scrollTo({ top: 0, behavior: "smooth" });
            setTimeout(() => navigate("/competitor/competitor-tables/details"), 2500);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };
    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={3}>
                <CrmPageHeader
                    title={isEditing ? "Edit Competitor" : "Add Competitor"}
                    subtitle="Manage your competitor information"
                />
                {successMessage && <CrmNotice sx={{ mb: 2 }}>{successMessage}</CrmNotice>}
                {readOnlyMode && (
                    <ReadOnlyBanner
                        message="This competitor is in read-only view. Click Edit to make changes."
                        onEdit={() => setReadOnlyMode(false)}
                    />
                )}
            </MDBox>
            <ReadOnlyFieldset readOnly={readOnlyMode}>
            <MDBox sx={{ ...crmCardSx, overflow: "visible", p: 3 }}>
                <form onSubmit={submitHandler}>
                    <Grid container spacing={2}>

                        {/* Company */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                label={t("Competitor Company Name")}
                                name="competitorCompany"
                                value={competitor.competitorCompany}
                                placeholder={t("Enter Competitor Company")}
                                fullWidth
                                onChange={handleChange}
                                sx={{
                                    "& .MuiInputBase-root": {height: 56}
                                }}
                            />
                        </Grid>

                        {/* Country Multiple */}
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                multiple
                                options={countryOptions}
                                getOptionLabel={(o) => o.label}
                                value={selectedCountries}
                                isOptionEqualToValue={(o, v) => o.label === v.label}
                                onChange={handleCountryChange}
                                renderOption={({ key: optionKey, ...props }, option) => (
                                    <li key={optionKey} {...props} style={{display: "flex", alignItems: "center"}}>
                                        {option.flagUrl && (
                                            <img
                                                src={option.flagUrl}
                                                width={20}
                                                height={15}
                                                style={{marginRight: 8}}
                                            />
                                        )}
                                        {option.label}
                                    </li>
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            key={option.label}
                                            label={option.label}
                                            variant="outlined"
                                            {...omitKey(getTagProps({ index }))}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Customer Country(ies)"
                                        placeholder="Select country(s)"
                                        fullWidth
                                        sx={{
                                            "& .MuiInputBase-root": {
                                                height: 56,
                                                display: "flex",
                                                alignItems: "center"
                                            }
                                        }}
                                    />
                                )}
                                disabled={loading}
                            />
                        </Grid>

                        {/* Representative */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Representative"
                                name="competitorRepresentative"
                                value={competitor.competitorRepresentative}
                                placeholder="Enter Representative"
                                fullWidth
                                onChange={handleChange}
                                sx={{
                                    "& .MuiInputBase-root": {height: 56}
                                }}
                            />
                        </Grid>

                        {/* Customers */}
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                multiple
                                options={customers}
                                getOptionLabel={(o) => o.name}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                value={selectedCustomer}
                                onChange={handleCustomerChange}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            key={option.id}
                                            label={option.name}
                                            variant="outlined"
                                            {...omitKey(getTagProps({ index }))}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Customer"
                                        placeholder="Select Customers"
                                        fullWidth
                                        sx={{
                                            "& .MuiInputBase-root": {
                                                height: 56,
                                                display: "flex",
                                                alignItems: "center"
                                            }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                                disabled={loading}
                            />
                        </Grid>

                        {/* Product Types */}
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                multiple
                                options={["Road", "Ocean", "Air", "Project Cargo", "Intermodal", "All"]}
                                value={competitor.competitorProduct}
                                onChange={(_e, newVal) => {
                                    const final =
                                        newVal.includes("All") && newVal.length > 1
                                            ? ["All"]
                                            : newVal.filter((v) => v !== "All");
                                    setCompetitor((p) => ({ ...p, competitorProduct: final }));
                                }}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip key={option} label={option} variant="outlined" {...omitKey(getTagProps({ index }))} />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Product Type(s)"
                                        fullWidth
                                        sx={{
                                            "& .MuiInputBase-root": {
                                                height: 56,
                                                display: "flex",
                                                alignItems: "center"
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Comment */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Comment"
                                name="competitorComment"
                                value={competitor.competitorComment}
                                placeholder="Enter Comment"
                                fullWidth
                                multiline
                                minRows={1}
                                onChange={handleChange}
                                sx={{
                                    "& .MuiInputBase-root": { minHeight: 56 }
                                }}
                            />
                        </Grid>

                        {/* Submit */}
                        <Grid item xs={12}>
                            <CrmActionButton type="submit" disabled={loading}>
                                {isEditing ? "Update Competitor" : "Create Competitor"}
                            </CrmActionButton>
                        </Grid>

                    </Grid>
                </form>
            </MDBox>
            </ReadOnlyFieldset>

            <br />
            <Footer />
        </DashboardLayout>
    );
};

export default CreateCompetitor;
